import '../../common/bn-flash';
import '../../common/bn-loading';
import '../../common/qr-scanner';
import '../../devices/props/device-serial-number';
import '../../devices/props/device-firmware-mode';
import '../../devices/props/device-time-since-last-event';
import { entityStore } from '~/utility/BinSentry-ui-utility';
import { bannerStyles } from '../../styles/banner-styles';
import { LitEntity, SirenActionMixin, html, css } from '../../utility/lit-entity';
import { sharedStyles } from '../../styles/shared-styles';

class WorkOrderDevice extends SirenActionMixin(LitEntity) {
  constructor() {
    super();
    this.deviceId = '';
    this.serialNumber = '';
  }

  static get properties() {
    return {
      deviceId: { type: String, attribute: false },
      serialNumber: { type: String, attribute: false },
    };
  }

  static get styles() {
    return [sharedStyles, bannerStyles, css`
      :host {
        display: block;
      }
      .action {
        margin-top: 1em;
      }
      .prase-error {
        font-weight: bold;
        color: var(--red-20);
      }
    `];
  }

  renderEntity() {
    return html`
      <bn-flash></bn-flash>
      ${this.renderDeviceInfo()}
      ${this.renderActions()}
    `;
  }

  renderDeviceInfo() {
    if (!this.deviceId) {
      return html`
        <div class="banner banner--warning">
          There is no device currently attached to this bin.
        </div>
      `;
    }

    return html`
      <bn-property label="Serial Number">
        <device-serial-number .href="${this.deviceId}"></device-serial-number>
      </bn-property>

      <bn-property label="Mode">
        <device-firmware-mode .href="${this.deviceId}"></device-firmware-mode>
      </bn-property>

      <bn-property label="Last Read">
        <device-time-since-last-event .href="${this.deviceId}"></device-time-since-last-event>
      </bn-property>
    `;
  }

  renderActions() {
    const actions = [];

    if (this.entity.hasAction('attach-device')) {
      actions.push(html`
        <div class="banner banner--info">
          Make sure the BinSentry device is turned on.
        </div>

        <p class="subdued">
          Use your mobile device's camera to scan the QR code located on the side of the device.
        </p>

        <qr-scanner
          @data="${this._parseDeviceQr}"
          @start="${this._start}"
          @stop="${this._stop}"
          @error="${(e) => this._error(e.detail.error.message)}"
        ></qr-scanner>

        <div class="manual-entry">
          <bn-form-field
            type="text"
            name="serialNumber"
            title="Device Serial Number"
            .value="${this.serialNumber}"
            @input="${this._onInputSerialNumber}"
          ></bn-form-field>
        </div>

        <div class="action">
          <button class="button button--contained" @click="${this._attachDevice}">Attach Device</button>
        </div>
      `);
    }

    if (this.entity.hasAction('remove-device')) {
      actions.push(html`
        <div class="action">
          <button
            class="button button--containedError"
            type="button"
            @click="${this._removeDevice}"
          >
            Remove Device
          </button>
        </div>
      `);
    }

    if (this.entity.hasAction('reassign-bin')) {
      actions.push(html`
        <div class="action">
          <h3>Reassign Bin</h3>
          <div class="banner banner--info">
            Make a copy of this bin in the target barn, and move the device to the newly created bin.
            This will create an installation request for the new bin.
          </div>
          <bn-action
            .href="${this.href}"
            actionname="reassign-bin"
            suppressheader
            @response="${() => entityStore.invalidateHref(this.href)}"
          ></bn-action>
        </div>
      `);
    }

    return actions;
  }

  _start() {
    const flash = this.shadowRoot.querySelector('bn-flash');
    flash.clear();
  }

  _stop() {
    const flash = this.shadowRoot.querySelector('bn-flash');
    flash.clear();
  }

  _error(error) {
    const flash = this.shadowRoot.querySelector('bn-flash');
    flash.clear();
    flash.critical(error);
  }

  _success(msg) {
    const flash = this.shadowRoot.querySelector('bn-flash');
    flash.clear();
    flash.success(msg);
  }

  _entityChanged(entity) {
    super._entityChanged(entity);
    const deviceLink = this.entity && this.entity.getSubEntityByClasses(['device', 'active']);
    if (deviceLink) {
      this.deviceId = deviceLink.href;
    }
  }

  _onInputSerialNumber(e) {
    this.serialNumber = e.target.input.value;

    clearTimeout(this.updateDeviceIdTimeout);
    this.updateDeviceIdTimeout = setTimeout(() => {
      if (this.deviceId.includes(this.serialNumber)) {
        // Serial number has not changed, do nothing
        return;
      }

      const apiBase = new URL(this.href).origin;
      this.deviceId = `${apiBase}/devices/${this.serialNumber}`;
    }, 2000);
  }

  async _attachDevice() {
    if (!this.deviceId || !this.entity) {
      return;
    }
    const action = this.entity.getActionByName('attach-device');
    action.getFieldByName('deviceId').value = this.deviceId;
    await this.submitAction(action);
    entityStore.invalidateHref(this.href);
  }

  async _removeDevice() {
    const action = this.entity.getActionByName('remove-device');
    await this.submitAction(action);
    entityStore.invalidateHref(this.href);
    this.deviceId = '';
  }

  async _parseDeviceQr(e) {
    if (this.processing) {
      return;
    }
    const { data } = e.detail;
    if (!data) {
      return;
    }
    try {
      this.processing = true;
      const url = new URL(data);
      if (!url.hostname.endsWith('binsentry.com')) {
        throw new Error('Unsupported hostname.');
      }
      if (!url.pathname.startsWith('/devices/')) {
        throw new Error('Incorrect path name.');
      }
      const deviceId = url.searchParams.get('deviceId');
      if (this.deviceId !== deviceId) {
        this.deviceId = deviceId;
        this.serialNumber = this.deviceId.split('/').pop();
        await this._attachDevice();
        this._success('The device has been successfully attached to the bin.');
      }
    } catch (ex) {
      this._error(html`
        The QR code scanned does not appear to be for a BinSentry device.
        <div><code>${ex.message}</code></div>
      `);
    } finally {
      this.processing = undefined;
    }
  }
}

window.customElements.define('work-order-device', WorkOrderDevice);
